/**
 * Dashboard widget catalogue and presets. Sizes match the artboard's
 * 12-column grid: S 3, M 4, L 6, XL 8, Full 12. A Full preset forces span 12.
 * Insights first keeps the ranked feed at span 12 while it is still XL.
 * S45 adds section widgets + Needs attention + the Everything preset.
 */

export const SIZE_SPAN = { S: 3, M: 4, L: 6, XL: 8, Full: 12 };
export const SIZES = ['S', 'M', 'L', 'XL', 'Full'];

export const SECTION_IDS = [
  'acquisition', 'monetisation', 'engagement', 'retention',
  'quality', 'store', 'releases', 'context',
];

export const WIDGETS = [
  { id: 'mau', title: 'Monthly actives', group: 'Metrics', desc: 'Users active in the last 30 days', size: 'S' },
  { id: 'act', title: 'Activation rate', group: 'Metrics', desc: 'Install to first key action', size: 'S' },
  { id: 'd7', title: 'D7 retention', group: 'Metrics', desc: 'Latest full cohort', size: 'S' },
  { id: 'paid', title: 'Trial → paid', group: 'Metrics', desc: 'Conversions from RevenueCat', size: 'S' },
  { id: 'insights', title: 'Ranked insights', group: 'Insights', desc: 'This week, ranked by impact', size: 'XL' },
  { id: 'attention', title: 'Needs attention', group: 'Insights', desc: 'Failed schedules, broken connectors, off-track targets', size: 'L' },
  { id: 'depth', title: 'Analysis depth', group: 'Product status', desc: 'What Bonnie can see', size: 'S' },
  { id: 'setup', title: 'Setup progress', group: 'Product status', desc: 'Steps left to finish setup', size: 'M' },
  { id: 'reports', title: 'Scheduled reports', group: 'Product status', desc: 'Recurring reports and their last run', size: 'M' },
  { id: 'conns', title: 'Connector health', group: 'Workspace', desc: 'Live status of each source', size: 'M' },
  { id: 'chats', title: 'Recent chats', group: 'Workspace', desc: 'Latest conversations', size: 'M' },
  { id: 'acquisition', title: 'Acquisition', group: 'Sections', desc: 'Where users come from', size: 'XL' },
  { id: 'monetisation', title: 'Monetisation', group: 'Sections', desc: 'Does it make money', size: 'XL' },
  { id: 'engagement', title: 'Activation and engagement', group: 'Sections', desc: 'Do new users get value', size: 'XL' },
  { id: 'retention', title: 'Retention', group: 'Sections', desc: 'Do they come back', size: 'XL' },
  { id: 'quality', title: 'Quality', group: 'Sections', desc: 'Is it working', size: 'XL' },
  { id: 'store', title: 'Store and reviews', group: 'Sections', desc: 'What users say', size: 'XL' },
  { id: 'releases', title: 'Releases and code', group: 'Sections', desc: 'What shipped', size: 'XL' },
  { id: 'context', title: 'Product context', group: 'Sections', desc: 'Goals, decisions, documents', size: 'XL' },
];

const GROUPS = ['Metrics', 'Insights', 'Product status', 'Workspace', 'Sections'];

const V2_APPEND = ['attention', ...SECTION_IDS];

function widgets(ids, sizeFor) {
  return ids.map((id, order) => ({
    id,
    size: typeof sizeFor === 'function' ? sizeFor(id) : sizeFor,
    order,
  }));
}

const SECTION_SIZE = (id) => (SECTION_IDS.includes(id) ? 'XL' : null);

export const PRESETS = {
  overview: {
    name: 'Overview',
    desc: 'KPIs in a row, insights beside status. The default.',
    widgets: widgets(
      ['mau', 'act', 'd7', 'depth', 'insights', 'reports', 'setup', 'attention',
        'acquisition', 'monetisation', 'engagement', 'retention',
        'quality', 'store', 'releases', 'context'],
      (id) => {
        const sizes = {
          insights: 'XL', reports: 'M', setup: 'M', attention: 'L',
        };
        SECTION_IDS.forEach((s) => { sizes[s] = 'XL'; });
        return sizes[id] || 'S';
      },
    ),
  },
  focus: {
    name: 'Insights first',
    desc: 'The ranked feed leads full width, KPIs follow.',
    widgets: widgets(
      ['insights', 'attention', 'mau', 'act', 'd7', 'depth', 'reports'],
      (id) => ({ insights: 'XL', attention: 'L', depth: 'L', reports: 'L' }[id] || 'M'),
    ),
  },
  compact: {
    name: 'Compact',
    desc: 'Every widget small. The most on one screen.',
    widgets: widgets(['mau', 'act', 'd7', 'paid', 'depth', 'setup', 'reports', 'conns', 'attention'], 'S'),
  },
  split: {
    name: 'Two columns',
    desc: 'Even halves. Easy to scan side by side.',
    widgets: widgets(['mau', 'act', 'insights', 'depth', 'reports', 'chats', 'conns', 'd7', 'attention'], 'L'),
  },
  full: {
    name: 'Full',
    desc: 'Every widget on its own row.',
    widgets: widgets(WIDGETS.map((widget) => widget.id), 'XL'),
  },
  everything: {
    name: 'Everything',
    desc: 'All available sections at full width.',
    widgets: widgets(
      ['mau', 'act', 'd7', 'depth', 'insights', 'attention', ...SECTION_IDS],
      (id) => (SECTION_IDS.includes(id) || id === 'insights' ? 'Full' : 'S'),
    ),
  },
};

export function widgetMeta(id) {
  return WIDGETS.find((widget) => widget.id === id) || { id, title: id, group: 'Workspace', desc: '', size: 'M' };
}

export function presetById(id) {
  return PRESETS[id] || PRESETS.overview;
}

export function layoutFromPreset(id) {
  const preset = presetById(id);
  return {
    preset: PRESETS[id] ? id : 'overview',
    widgets: preset.widgets.map((widget) => ({ ...widget })),
  };
}

/** @param {{ id: string, size: string }} widget @param {string} preset */
export function spanFor(widget, preset) {
  if (preset === 'full') return 12;
  if (preset === 'everything' && (SECTION_IDS.includes(widget.id) || widget.id === 'insights')) return 12;
  if (preset === 'focus' && widget.id === 'insights' && widget.size === 'XL') return 12;
  if (widget.size === 'Full') return 12;
  return SIZE_SPAN[widget.size] || SIZE_SPAN.S;
}

/** Overview insights sits beside the two status cards. */
export function rowsFor(widget, preset) {
  if (preset === 'overview' && widget.id === 'insights' && widget.size === 'XL') return 2;
  return 1;
}

export function sortedWidgets(widgetsOnBoard) {
  return (widgetsOnBoard || []).slice().sort((a, b) => a.order - b.order);
}

export function cycleSize(size) {
  const index = SIZES.indexOf(size);
  return SIZES[index === -1 ? 0 : (index + 1) % SIZES.length];
}

export function moveWidget(widgetsOnBoard, id, delta) {
  const order = sortedWidgets(widgetsOnBoard);
  const from = order.findIndex((widget) => widget.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = order.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((widget, index) => ({ ...widget, order: index }));
}

export function removeWidget(widgetsOnBoard, id) {
  return sortedWidgets(widgetsOnBoard)
    .filter((widget) => widget.id !== id)
    .map((widget, index) => ({ ...widget, order: index }));
}

export function addWidget(widgetsOnBoard, id) {
  const current = sortedWidgets(widgetsOnBoard);
  if (current.some((widget) => widget.id === id)) return current;
  const meta = WIDGETS.find((widget) => widget.id === id);
  if (!meta) return current;
  return current.concat({ id, size: meta.size, order: current.length });
}

export function resizeWidget(widgetsOnBoard, id) {
  return sortedWidgets(widgetsOnBoard).map((widget) => (
    widget.id === id ? { ...widget, size: cycleSize(widget.size) } : widget
  ));
}

export function groupsFor(widgetsOnBoard) {
  const on = new Set((widgetsOnBoard || []).map((widget) => widget.id));
  return GROUPS.map((name) => {
    const items = WIDGETS.filter((widget) => widget.group === name);
    return {
      name,
      total: items.length,
      on: items.filter((widget) => on.has(widget.id)).length,
      items,
    };
  });
}

/**
 * Append new v2 widgets to a saved layout once so arrangements survive.
 * @param {{ preset: string, widgets: object[] }} layout
 * @param {{ available?: string[] }} [opts]
 */
export function appendV2Widgets(layout, opts = {}) {
  const widgets = sortedWidgets(layout && layout.widgets);
  const have = new Set(widgets.map((w) => w.id));
  const available = opts.available ? new Set(opts.available) : null;
  const defaults = {
    attention: 'L',
    acquisition: 'XL',
    monetisation: 'XL',
    engagement: 'XL',
    retention: 'XL',
    quality: 'XL',
    store: 'XL',
    releases: 'XL',
    context: 'XL',
  };
  let migrated = false;
  for (const id of V2_APPEND) {
    if (have.has(id)) continue;
    if (available && SECTION_IDS.includes(id) && !available.has(id)) continue;
    if (!WIDGETS.some((w) => w.id === id)) continue;
    widgets.push({ id, size: defaults[id] || 'XL', order: widgets.length });
    have.add(id);
    migrated = true;
  }
  return {
    preset: (layout && layout.preset) || 'overview',
    widgets: widgets.map((w, i) => ({ ...w, order: i })),
    migrated,
  };
}

export { SECTION_SIZE };
